import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase, clearPaymentStatusCache } from '../../services/supabase';
import Header from '../../components/Header';
import logger from '../../utils/logger';
import '../../styles/components/payment.css';
import '../../styles/components/layout.css';

const PaymentPage = () => {
    const navigate = useNavigate();
    const { user, paymentApproved } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [cardForm, setCardForm] = useState(null);
    const [scriptLoaded, setScriptLoaded] = useState(false);
    const formRef = useRef(null);

    // Efeito exclusivo para redirecionamento
    useEffect(() => {
        if (paymentApproved) {
            logger.payment('Pagamento aprovado, redirecionando para dashboard');
            navigate('/dashboard', { replace: true });
        }
    }, [paymentApproved, navigate]);

    // Efeito para carregamento do script e inicialização do formulário
    useEffect(() => {
        // Se o pagamento já estiver aprovado, não faz nada
        if (paymentApproved) {
            return;
        }

        const loadMercadoPagoScript = async () => {
            if (scriptLoaded) {
                logger.debug('Script do MercadoPago já carregado');
                return true;
            }

            try {
                const script = document.createElement('script');
                script.src = 'https://sdk.mercadopago.com/js/v2';
                script.type = 'text/javascript';
                
                await new Promise((resolve, reject) => {
                    script.onload = () => {
                        setScriptLoaded(true);
                        resolve();
                    };
                    script.onerror = reject;
                    document.body.appendChild(script);
                });

                return true;
            } catch (error) {
                logger.error('Erro ao carregar script do MercadoPago:', error);
                return false;
            }
        };

        const initializeForm = async () => {
            if (!window.MercadoPago || !formRef.current) {
                logger.debug('MercadoPago ou formRef não disponível');
                return;
            }

            try {
                logger.payment('Inicializando formulário...');
                const mp = new window.MercadoPago(import.meta.env.VITE_MP_PUBLIC_KEY, {
                    locale: 'pt-BR'
                });

                const cardForm = mp.cardForm({
                    amount: "89.90",
                    iframe: true,
                    form: {
                        id: "form-checkout",
                        cardNumber: {
                            id: "form-checkout__cardNumber",
                            placeholder: "Número do cartão",
                        },
                        expirationDate: {
                            id: "form-checkout__expirationDate",
                            placeholder: "MM/YY",
                        },
                        securityCode: {
                            id: "form-checkout__securityCode",
                            placeholder: "Código de segurança",
                        },
                        cardholderName: {
                            id: "form-checkout__cardholderName",
                            placeholder: "Titular do cartão",
                        },
                        issuer: {
                            id: "form-checkout__issuer",
                            placeholder: "Banco emissor",
                        },
                        installments: {
                            id: "form-checkout__installments",
                            placeholder: "Parcelas",
                        },
                        identificationType: {
                            id: "form-checkout__identificationType",
                            placeholder: "Tipo de documento",
                        },
                        identificationNumber: {
                            id: "form-checkout__identificationNumber",
                            placeholder: "Número do documento",
                        },
                        cardholderEmail: {
                            id: "form-checkout__cardholderEmail",
                            placeholder: "E-mail",
                        },
                    },
                    callbacks: {
                        onFormMounted: error => {
                            if (error) {
                                logger.error('Erro ao montar formulário:', error);
                                return;
                            }
                            logger.payment('Formulário montado com sucesso');
                        },
                        onFormUnmounted: error => {
                            if (error) {
                                logger.error('Erro ao desmontar formulário:', error);
                            }
                        },
                        onIdentificationTypesReceived: (error, identificationTypes) => {
                            if (error) {
                                logger.error('Erro ao receber tipos de identificação:', error);
                                return;
                            }
                            logger.debug('Tipos de identificação recebidos:', identificationTypes);
                        },
                        onPaymentMethodsReceived: (error, paymentMethods) => {
                            if (error) {
                                logger.error('Erro ao receber métodos de pagamento:', error);
                                return;
                            }
                            logger.debug('Métodos de pagamento recebidos:', paymentMethods);
                        },
                        onIssuersReceived: (error, issuers) => {
                            if (error) {
                                logger.error('Erro ao receber emissores:', error);
                                return;
                            }
                            logger.debug('Emissores recebidos:', issuers);
                        },
                        onInstallmentsReceived: (error, installments) => {
                            if (error) {
                                logger.error('Erro ao receber parcelas:', error);
                                return;
                            }
                            logger.debug('Parcelas recebidas:', installments);
                        },
                        onCardTokenReceived: (error, token) => {
                            if (error) {
                                logger.error('Erro ao receber token do cartão:', error);
                                return;
                            }
                            logger.debug('Token do cartão recebido');
                        },
                        onSubmit: event => {
                            event.preventDefault();
                            if (isProcessing) {
                                logger.payment('Pagamento já está em processamento');
                                return;
                            }
                            handleSubmitPayment(cardForm);
                        },
                        onFetching: (resource) => {
                            logger.debug('Buscando recurso:', resource);
                        }
                    },
                });
                
                logger.payment('Instância do formulário criada com sucesso');
                setCardForm(cardForm);
            } catch (error) {
                logger.error('Erro ao inicializar formulário:', error);
            }
        };

        const setup = async () => {
            if (paymentApproved) {
                logger.payment('Pagamento já aprovado, não inicializando formulário');
                return;
            }

            const scriptSuccess = await loadMercadoPagoScript();
            if (scriptSuccess && !paymentApproved) {
                let attempts = 0;
                const maxAttempts = 5;
                const tryInitialize = () => {
                    if (paymentApproved) {
                        logger.payment('Pagamento aprovado durante inicialização, abortando...');
                        return;
                    }
                    
                    if (attempts >= maxAttempts) {
                        logger.error('Máximo de tentativas excedido ao inicializar formulário');
                        return;
                    }
                    attempts++;
                    initializeForm();
                    if (!formRef.current) {
                        setTimeout(tryInitialize, 500);
                    }
                };
                tryInitialize();
            }
        };

        setup();

        return () => {
            try {
                if (cardForm) {
                    cardForm.unmount();
                }
                formRef.current = null;
                setCardForm(null);
                const script = document.querySelector('script[src="https://sdk.mercadopago.com/js/v2"]');
                if (script) {
                    document.body.removeChild(script);
                }
            } catch (error) {
                logger.error('Erro ao limpar recursos:', error);
            }
        };
    }, [paymentApproved]);

    const handleSubmitPayment = async (cardFormInstance) => {
        if (isProcessing) {
            logger.payment('Pagamento já está em processamento');
            return;
        }

        setIsProcessing(true);
        setError(null);
        setSuccess(false);

        try {
            logger.payment('Iniciando processamento do pagamento');
            const formData = await cardFormInstance.getCardFormData();
            const response = await fetch('/api/process_payment', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    ...formData,
                    userId: user.id
                }),
            });

            const result = await response.json();

            if (response.ok) {
                logger.payment('Pagamento processado com sucesso');
                setSuccess(true);
                clearPaymentStatusCache();
                setTimeout(() => {
                    navigate('/dashboard', { replace: true });
                }, 2000);
            } else {
                logger.error('Erro ao processar pagamento:', result.error);
                setError(result.error);
            }
        } catch (error) {
            logger.error('Erro durante processamento do pagamento:', error);
            setError('Ocorreu um erro ao processar o pagamento. Por favor, tente novamente.');
        } finally {
            setIsProcessing(false);
        }
    };

    if (loading) {
        return <div>Carregando...</div>;
    }

    return (
        <div className="payment-page">
            <Header />
            <div className="payment-container">
                <form id="form-checkout" ref={formRef}>
                    <div id="form-checkout__cardNumber" />
                    <div id="form-checkout__expirationDate" />
                    <div id="form-checkout__securityCode" />
                    <input type="text" id="form-checkout__cardholderName" />
                    <select id="form-checkout__issuer">
                        <option value="">Selecione o banco</option>
                    </select>
                    <select id="form-checkout__installments">
                        <option value="">Selecione as parcelas</option>
                    </select>
                    <select id="form-checkout__identificationType">
                        <option value="">Selecione o tipo</option>
                    </select>
                    <input type="text" id="form-checkout__identificationNumber" />
                    <input type="email" id="form-checkout__cardholderEmail" />
                    <button type="submit" id="form-checkout__submit">Pagar</button>
                    {error && <div className="error-message">{error}</div>}
                    {success && <div className="success-message">Pagamento realizado com sucesso!</div>}
                </form>
            </div>
        </div>
    );
};

export default PaymentPage;